import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type {
  AnnotationLabel,
  Annotation,
  AnnotationQueueItem,
  DefectType,
  Region,
  Verdict,
} from '@visionqc/shared';
import { api } from '../api/client';
import { useImageUrl } from '../api/useImageUrl';
import { useProducts } from '../api/useProducts';

interface PrefillState {
  imageKey?: string;
  inspectionId?: string;
  productId?: number;
  suggestedLabel?: Verdict;
  /** Set to work through, from Dataset -> "Annotate this set". */
  setId?: number;
  setName?: string;
}

/** A unit of work in the annotator — either a queue item or a prefilled image. */
interface WorkItem {
  imageKey: string;
  inspectionId: string | null;
  productId: number;
  score?: number;
  suggestedLabel?: Verdict;
}

/** Accent blue, literal — see colorFor for why this can't be a CSS variable. */
const UNCLASSIFIED_COLOR = '#3b82f6';

const LABEL_CLASS: Record<AnnotationLabel, string> = {
  GOOD: 'good',
  BAD: 'bad',
  UNSURE: 'unsure',
};

export function AnnotateView() {
  const location = useLocation();
  const prefill = (location.state ?? {}) as PrefillState;

  const [queue, setQueue] = useState<WorkItem[]>([]);
  const [index, setIndex] = useState(0);
  const [defectTypes, setDefectTypes] = useState<DefectType[]>([]);
  const [label, setLabel] = useState<AnnotationLabel | null>(null);
  const [activeDefect, setActiveDefect] = useState<number | null>(null);
  const [regions, setRegions] = useState<Region[]>([]);
  // Which region the defect buttons apply to. null = the image as a whole.
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [savedCount, setSavedCount] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  // The annotation this image already carries, if any.
  const [existing, setExisting] = useState<Annotation | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterProduct, setFilterProduct] = useState<number | undefined>(prefill.productId);
  // When arriving from a dataset, work through THAT set first rather than the
  // whole library.
  const [filterSet, setFilterSet] = useState<number | undefined>(prefill.setId);
  const { products } = useProducts();

  const item = queue[index] ?? null;

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const items = await api.annotationQueue(filterProduct, 60, filterSet);
      const mapped: WorkItem[] = items.map((q: AnnotationQueueItem) => ({
        imageKey: q.imageKey,
        inspectionId: q.id,
        productId: q.productId,
        score: q.score,
      }));
      if (prefill.imageKey && prefill.productId) {
        mapped.unshift({
          imageKey: prefill.imageKey,
          inspectionId: prefill.inspectionId ?? null,
          productId: prefill.productId,
          suggestedLabel: prefill.suggestedLabel,
        });
      }
      setQueue(mapped);
      setIndex(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProduct, filterSet]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const loadDefectTypes = useCallback(async (productId: number) => {
    const types = await api.listDefectTypes(productId).catch(() => []);
    setDefectTypes(types);
  }, []);

  useEffect(() => {
    if (item) void loadDefectTypes(item.productId);
  }, [item?.productId, loadDefectTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Load whatever is already known about this image.
   *
   * A saved annotation used to be write-only: the shapes lived in the database
   * but nothing showed them back, so a wrong label could be spotted and never
   * corrected. Reopening an annotated image now restores its marks, and saving
   * again records the correction.
   */
  useEffect(() => {
    setLabel(item?.suggestedLabel ?? null);
    setActiveDefect(null);
    setRegions([]);
    setSelectedRegion(null);
    setExisting(null);
    if (!item) return;

    let cancelled = false;
    void api
      .annotationByImage(item.imageKey)
      .then((a) => {
        // The item may have moved on while the request was in flight; applying
        // a stale answer would label the wrong picture.
        if (cancelled || !a) return;
        setExisting(a);
        setLabel(a.label);
        setActiveDefect(a.defectTypeId ?? null);
        setRegions((a.regions ?? []) as Region[]);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [index, item?.imageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = useCallback(() => {
    setIndex((i) => {
      const next = i + 1;
      if (next >= queue.length) void loadQueue();
      return next;
    });
  }, [queue.length, loadQueue]);

  /**
   * Picking a named defect also sets the outcome its classification implies.
   *
   * If a shape is selected the defect lands on that shape, so one image can
   * carry a scratch here and a dent there. With nothing selected it applies to
   * the image as a whole, which is the common case and stays a single click.
   */
  const pickDefect = useCallback(
    (dt: DefectType) => {
      if (selectedRegion !== null) {
        setRegions((prev) =>
          prev.map((r, i) => (i === selectedRegion ? { ...r, defect_type_id: dt.id } : r)),
        );
      } else {
        setActiveDefect(dt.id);
      }
      setLabel(dt.classification);
    },
    [selectedRegion],
  );

  const save = useCallback(
    async (labelOverride?: AnnotationLabel) => {
      const finalLabel = labelOverride ?? label;
      if (!item || !finalLabel) return;
      try {
        await api.createAnnotation({
          inspectionId: item.inspectionId,
          imageKey: item.imageKey,
          label: finalLabel,
          defectTypeId: finalLabel === 'GOOD' ? null : activeDefect,
          regions: finalLabel === 'GOOD' ? [] : regions,
          annotatedBy: 'operator',
        });
      } catch (e) {
        // A failed save used to be completely silent — the click just did
        // nothing, which is indistinguishable from a save that worked. Stay on
        // the image so the work isn't lost.
        setSaveError((e as Error).message);
        return;
      }
      setSaveError(null);
      setSavedCount((c) => c + 1);
      advance();
    },
    [item, label, activeDefect, regions, advance],
  );

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
      const k = e.key.toLowerCase();
      if (k === 'g') void save('GOOD');
      else if (k === 'u') setLabel('UNSURE');
      else if (k === 'b') setLabel('BAD');
      else if (e.key === 'Enter' || k === 's') void save();
      else if (e.key >= '1' && e.key <= '9') {
        const dt = defectTypes[Number(e.key) - 1];
        if (dt) pickDefect(dt);
      } else if (e.key === 'ArrowRight') advance();
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      else if (e.key === 'Escape') setSelectedRegion(null);
      else if (e.key === 'Backspace') {
        // Delete the selected shape if there is one, else the last drawn.
        setRegions((r) =>
          selectedRegion !== null ? r.filter((_, k) => k !== selectedRegion) : r.slice(0, -1),
        );
        setSelectedRegion(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, advance, defectTypes, pickDefect, selectedRegion]);

  if (loading) return <div className="spinner">Loading annotation queue…</div>;

  if (!item) {
    return (
      <div>
        <h1 className="view-title">Annotate</h1>
        <div className="panel">
          <p>
            {filterSet ? 'This set is fully annotated.' : 'Queue empty — every image has been annotated.'}
          </p>
          {filterSet && (
            <button className="btn primary" style={{ marginRight: 8 }} onClick={() => setFilterSet(undefined)}>
              Annotate the rest of the library
            </button>
          )}
          <p className="muted">You annotated {savedCount} images this session.</p>
          <button className="btn" onClick={() => void loadQueue()}>
            Refresh queue
          </button>
        </div>
      </div>
    );
  }


  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">
          Annotate
          {filterSet && prefill.setName && (
            <span className="muted" style={{ fontSize: 15, fontWeight: 400, marginLeft: 10 }}>
              · {prefill.setName}
            </span>
          )}
        </h1>
        <div className="row">
          {filterSet && (
            <button className="btn sm" onClick={() => setFilterSet(undefined)} title="Include every unannotated image">
              ✕ Set filter
            </button>
          )}
          <select
            value={filterProduct ?? ''}
            onChange={(e) => setFilterProduct(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">All products</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <div className="muted">
            Saved: <strong>{savedCount}</strong> · {index + 1}/{queue.length}
            {item.score !== undefined && <> · score {item.score.toFixed(3)}</>}
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '170px minmax(0, 1fr) 360px' }}>
        <QueueBrowser queue={queue} index={index} onSelect={setIndex} />

        <AnnotatorCanvas
          imageKey={item.imageKey}
          regions={regions}
          defectTypes={defectTypes}
          activeDefect={activeDefect}
          onAddRegion={(r) => {
            setRegions((prev) => [...prev, r]);
            // Marking something on the part is itself the verdict: it isn't
            // good. BAD is the default rather than UNSURE because a deliberate
            // mark is a positive finding, and it stays one click to change.
            setLabel((cur) => cur ?? 'BAD');
          }}
          selected={selectedRegion}
          onSelect={setSelectedRegion}
        />

        <div className="grid" style={{ alignContent: 'start', gap: 14 }}>
          {/* Quick outcomes */}
          <div className="panel">
            <label>Verdict</label>
            <div className="grid" style={{ gap: 8 }}>
              <button
                className={`label-btn good ${label === 'GOOD' ? 'selected' : ''}`}
                onClick={() => void save('GOOD')}
              >
                ✓ GOOD <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>no defect</span>
                <span className="kbd" style={{ marginLeft: 'auto' }}>G</span>
              </button>
              {/* BAD used to be reachable only by picking a named defect or
                  knowing the keyboard shortcut. That is fine when a product has
                  a defect vocabulary and wrong the moment it doesn't — the one
                  verdict a QC tool exists to record had no button. */}
              <button
                className={`label-btn bad ${label === 'BAD' ? 'selected' : ''}`}
                onClick={() => setLabel('BAD')}
              >
                ■ BAD{' '}
                <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>
                  defect — name it below, or just save
                </span>
                <span className="kbd" style={{ marginLeft: 'auto' }}>B</span>
              </button>
              <button
                className={`label-btn unsure ${label === 'UNSURE' ? 'selected' : ''}`}
                onClick={() => setLabel('UNSURE')}
              >
                ? UNSURE <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>needs review</span>
                <span className="kbd" style={{ marginLeft: 'auto' }}>U</span>
              </button>
            </div>
          </div>

          {/* Product-specific defect vocabulary */}
          <DefectVocabulary
            productId={item.productId}
            defectTypes={defectTypes}
            activeDefect={activeDefect}
            onPick={pickDefect}
            onCreated={() => void loadDefectTypes(item.productId)}
          />

          {regions.length > 0 && (
            <MarkedShapes
              regions={regions}
              defectTypes={defectTypes}
              selected={selectedRegion}
              onSelect={setSelectedRegion}
              onRemove={(i) => {
                setRegions((prev) => prev.filter((_, k) => k !== i));
                setSelectedRegion(null);
              }}
            />
          )}

          <div className="panel">
            <button
              className="btn primary"
              style={{ width: '100%', fontSize: 18, padding: '16px' }}
              onClick={() => void save()}
              disabled={!label}
            >
              Save &amp; next <span className="kbd">Enter</span>
            </button>
            {/* A classification annotation has no quality label — it names a
                class instead. This page is the anomaly one, so the badge only
                appears when there is a verdict to show. */}
            {existing?.label && (
              <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>
                Already annotated as{' '}
                <span className={`badge ${LABEL_CLASS[existing.label]}`}>{existing.label}</span> —
                saving records your correction.
              </div>
            )}
            {saveError && (
              <div style={{ marginTop: 10, fontSize: 13, color: 'var(--bad)' }}>
                ■ Not saved: {saveError}. Your marks are still here — try again.
              </div>
            )}
            {label && (
              <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>
                Saving as <span className={`badge ${LABEL_CLASS[label]}`}>{label}</span>
                {activeDefect && defectTypes.find((d) => d.id === activeDefect) && (
                  <> · {defectTypes.find((d) => d.id === activeDefect)!.name}</>
                )}
                {regions.length > 0 && <> · {regions.length} box{regions.length > 1 ? 'es' : ''}</>}
              </div>
            )}
            <div className="row" style={{ marginTop: 10, justifyContent: 'space-between' }}>
              <button className="btn sm" onClick={() => setIndex((i) => Math.max(0, i - 1))}>
                ← Prev
              </button>
              {/* Some images should never have been captured — a blurred frame,
                  a test shot, the whole batch a load test left behind. Skipping
                  only defers them; they come back next time the queue loads. */}
              <button
                className="btn sm"
                title="Delete this image and everything attached to it"
                onClick={async () => {
                  if (!item.inspectionId) return;
                  if (!window.confirm('Delete this image for good? Its annotation goes too.')) return;
                  try {
                    await api.deleteInspections({ ids: [item.inspectionId] });
                    setQueue((q) => q.filter((_, k) => k !== index));
                  } catch (e) {
                    setSaveError((e as Error).message);
                  }
                }}
              >
                Delete
              </button>
              <button className="btn sm" onClick={advance}>
                Skip →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The product's defect vocabulary. Names are the primary affordance — each is
 * colored, carries its own GOOD/BAD/UNSURE meaning, and can be created inline so
 * you never have to leave the annotator to add a new defect kind.
 */
function DefectVocabulary({
  productId,
  defectTypes,
  activeDefect,
  onPick,
  onCreated,
}: {
  productId: number;
  defectTypes: DefectType[];
  activeDefect: number | null;
  onPick: (dt: DefectType) => void;
  onCreated: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#ff3b30');
  const [classification, setClassification] = useState<AnnotationLabel>('BAD');
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    if (!name.trim()) return;
    setError(null);
    try {
      await api.createDefectType(productId, {
        name: name.trim(),
        color,
        classification,
        sortOrder: defectTypes.length,
      });
      setName('');
      setAdding(false);
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <label style={{ margin: 0 }}>Defect type</label>
        <button className="btn sm" onClick={() => setAdding((a) => !a)}>
          {adding ? '✕' : '+ New'}
        </button>
      </div>

      {adding && (
        <div className="grid" style={{ gap: 8, marginBottom: 12 }}>
          <input
            autoFocus
            placeholder="Defect name (e.g. hairline scratch)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void create()}
          />
          <div className="row">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{ width: 46, padding: 4 }}
            />
            <select
              value={classification}
              onChange={(e) => setClassification(e.target.value as AnnotationLabel)}
            >
              <option value="BAD">means BAD</option>
              <option value="GOOD">means GOOD</option>
              <option value="UNSURE">means UNSURE</option>
            </select>
            <button className="btn primary sm" onClick={create} disabled={!name.trim()}>
              Add
            </button>
          </div>
          {error && <div style={{ color: 'var(--bad)', fontSize: 13 }}>{error}</div>}
        </div>
      )}

      <div className="grid" style={{ gap: 8 }}>
        {defectTypes.map((dt, i) => (
          <button
            key={dt.id}
            className={`defect-btn ${activeDefect === dt.id ? 'selected' : ''}`}
            style={activeDefect === dt.id ? { borderColor: dt.color } : undefined}
            onClick={() => onPick(dt)}
          >
            <span className="defect-swatch" style={{ background: dt.color }} />
            <span className="defect-name">
              {dt.name}
              {dt.description && <span className="defect-desc">{dt.description}</span>}
            </span>
            <span className={`class-chip ${LABEL_CLASS[dt.classification]}`}>{dt.classification}</span>
            {i < 9 && <span className="kbd">{i + 1}</span>}
          </button>
        ))}
        {defectTypes.length === 0 && !adding && (
          <span className="muted" style={{ fontSize: 14 }}>
            No defect types for this product yet — add one with <strong>+ New</strong> (or in Settings).
          </span>
        )}
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
        Picking a defect also sets the verdict it implies. Drag on the image to mark where it is;{' '}
        <span className="kbd">Backspace</span> removes the last box.
      </p>
    </div>
  );
}

/** Thumbnail browser for the annotation queue. */
function QueueBrowser({
  queue,
  index,
  onSelect,
}: {
  queue: WorkItem[];
  index: number;
  onSelect: (i: number) => void;
}) {
  return (
    <div className="panel" style={{ padding: 10, maxHeight: '80vh', overflowY: 'auto' }}>
      <div
        className="muted"
        style={{ fontSize: 12, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}
      >
        Queue · {queue.length}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {queue.map((q, i) => (
          <QueueThumb
            key={`${q.imageKey}-${i}`}
            item={q}
            selected={i === index}
            onClick={() => onSelect(i)}
          />
        ))}
      </div>
      {queue.length === 0 && <div className="muted" style={{ fontSize: 13 }}>Queue empty.</div>}
    </div>
  );
}

function QueueThumb({
  item,
  selected,
  onClick,
}: {
  item: WorkItem;
  selected: boolean;
  onClick: () => void;
}) {
  const url = useImageUrl(item.imageKey);
  return (
    <button
      onClick={onClick}
      title={item.score !== undefined ? `score ${item.score.toFixed(3)}` : item.imageKey}
      style={{
        padding: 0,
        border: selected ? '2px solid var(--accent)' : '2px solid transparent',
        borderRadius: 6,
        background: 'var(--bg)',
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        aspectRatio: '1',
      }}
    >
      {url && <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
      {item.score !== undefined && (
        <span
          style={{
            position: 'absolute',
            bottom: 2,
            right: 3,
            fontSize: 10,
            fontFamily: 'var(--mono)',
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            padding: '1px 4px',
            borderRadius: 4,
          }}
        >
          {item.score.toFixed(2)}
        </span>
      )}
    </button>
  );
}

/**
 * The shapes marked on this image, each with the defect it carries.
 *
 * Without this the shapes only existed on the canvas, so an unclassified one
 * was invisible until you happened to click it — and an unclassified region is
 * exactly what you must not save by accident.
 */
function MarkedShapes({
  regions,
  defectTypes,
  selected,
  onSelect,
  onRemove,
}: {
  regions: Region[];
  defectTypes: DefectType[];
  selected: number | null;
  onSelect: (i: number | null) => void;
  onRemove: (i: number) => void;
}) {
  const unclassified = regions.filter((r) => !r.defect_type_id).length;

  return (
    <div className="panel">
      <label>
        Marked shapes <span className="muted">({regions.length})</span>
      </label>
      <div className="grid" style={{ gap: 6 }}>
        {regions.map((r, i) => {
          const dt = defectTypes.find((d) => d.id === r.defect_type_id);
          return (
            <div
              key={i}
              className="row"
              onClick={() => onSelect(i === selected ? null : i)}
              style={{
                gap: 8,
                padding: '7px 9px',
                borderRadius: 8,
                cursor: 'pointer',
                background: i === selected ? 'var(--panel-2)' : 'transparent',
                border: `1px solid ${i === selected ? 'var(--accent)' : 'var(--border)'}`,
              }}
            >
              <span style={{ fontSize: 15 }}>{r.type === 'ellipse' ? '◯' : '▭'}</span>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: dt?.color ?? 'var(--border)',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 13 }}>
                {dt ? dt.name : <span className="muted">Not classified yet</span>}
              </span>
              <button
                className="btn sm"
                style={{ marginLeft: 'auto' }}
                title="Remove this shape"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(i);
                }}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '10px 0 0' }}>
        {selected !== null
          ? 'Pick a defect below to classify the selected shape.'
          : unclassified > 0
            ? `${unclassified} shape${unclassified > 1 ? 's have' : ' has'} no defect yet — click one, then pick a defect.`
            : 'Click a shape to change its defect.'}
      </p>
    </div>
  );
}

/** Zoomable/pannable image with normalized shape drawing via an SVG overlay. */
function AnnotatorCanvas({
  imageKey,
  regions,
  defectTypes,
  activeDefect,
  onAddRegion,
  selected,
  onSelect,
}: {
  imageKey: string;
  regions: Region[];
  defectTypes: DefectType[];
  activeDefect: number | null;
  onAddRegion: (r: Region) => void;
  selected: number | null;
  onSelect: (i: number | null) => void;
}) {
  const url = useImageUrl(imageKey);
  const imgRef = useRef<HTMLImageElement>(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [draft, setDraft] = useState<Region | null>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const panStart = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  // Which shape the next drag produces. Round defects — pits, blowholes,
  // droplets — are far quicker to trace with an ellipse than a rectangle.
  const [shape, setShape] = useState<'bbox' | 'ellipse'>('bbox');

  /**
   * A shape's colour, as a literal.
   *
   * It must NOT be a `var(--x)`: this is an SVG presentation attribute, and CSS
   * variables are not resolved there — the paint is invalid and the shape falls
   * back to black, which is invisible on a dark part. That is exactly what an
   * unclassified shape looked like once drawing stopped requiring a defect
   * first.
   */
  const colorFor = (id: number | null | undefined) =>
    defectTypes.find((d) => d.id === id)?.color ?? UNCLASSIFIED_COLOR;

  const toNorm = (clientX: number, clientY: number) => {
    const rect = imgRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  };

  const onWheel = (e: React.WheelEvent) => {
    const delta = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    setView((v) => ({ ...v, scale: Math.min(8, Math.max(1, v.scale * delta)) }));
  };

  const onMouseDown = (e: React.MouseEvent) => {
    // Without this the browser starts its own image/text drag: you get a ghost
    // of the picture following the cursor and no mousemove events, which looks
    // exactly like "I can only drag the image".
    e.preventDefault();
    if (e.button === 1 || e.altKey) {
      panStart.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
      return;
    }
    const p = toNorm(e.clientX, e.clientY);
    dragStart.current = p;
    setDraft({ type: shape, x: p.x, y: p.y, w: 0, h: 0, defect_type_id: activeDefect ?? null });
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (panStart.current) {
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      setView((v) => ({ ...v, x: panStart.current!.vx + dx, y: panStart.current!.vy + dy }));
      return;
    }
    if (dragStart.current) {
      const p = toNorm(e.clientX, e.clientY);
      const s = dragStart.current;
      setDraft({
        type: shape,
        x: Math.min(s.x, p.x),
        y: Math.min(s.y, p.y),
        w: Math.abs(p.x - s.x),
        h: Math.abs(p.y - s.y),
        defect_type_id: activeDefect ?? null,
      });
    }
  };

  const onMouseUp = () => {
    panStart.current = null;
    if (draft && dragStart.current) {
      // Selecting what you just drew is what makes "draw, then classify" work:
      // the defect list on the right immediately applies to this shape.
      if (draft.w > 0.01 && draft.h > 0.01) {
        onAddRegion(draft);
        onSelect(regions.length);
      }
      setDraft(null);
      dragStart.current = null;
    }
  };

  const all = draft ? [...regions, draft] : regions;

  return (
    <div className="panel" style={{ overflow: 'hidden' }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="row" style={{ gap: 10 }}>
          <div className="row" style={{ gap: 4 }}>
            <button
              className={`btn sm${shape === 'bbox' ? ' primary' : ''}`}
              onClick={() => setShape('bbox')}
              title="Draw rectangles (R)"
            >
              ▭ Box
            </button>
            <button
              className={`btn sm${shape === 'ellipse' ? ' primary' : ''}`}
              onClick={() => setShape('ellipse')}
              title="Draw ellipses (C) — better for pits, blowholes and droplets"
            >
              ◯ Circle
            </button>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>
            Drag to mark · click a shape to classify it · Alt-drag to pan · scroll to zoom
          </span>
        </div>
        <button className="btn sm" onClick={() => setView({ scale: 1, x: 0, y: 0 })}>
          Reset view
        </button>
      </div>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: '#000',
          borderRadius: 8,
          cursor: 'crosshair',
          userSelect: 'none',
          // Belt and braces: kill any native drag that still tries to start.
          WebkitUserSelect: 'none',
        }}
        onDragStart={(e) => e.preventDefault()}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <div
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            transformOrigin: 'top left',
            position: 'relative',
          }}
        >
          {url ? (
            <img
              ref={imgRef}
              src={url}
              alt="to annotate"
              style={{ display: 'block', width: '100%', userSelect: 'none' }}
              draggable={false}
            />
          ) : (
            <div style={{ height: 400 }} />
          )}
          <svg
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {/*
              Visibility can't rest on the chosen hue: a dark red box vanishes on
              a black bearing, a light one on a pale cookie. Each box is drawn
              twice — a dark halo underneath, the defect colour on top — so the
              edge stays legible on any product, the way subtitles do.
            */}
            {all.map((r, i) => {
              const c = colorFor(r.defect_type_id);
              const isSelected = i === selected;
              // A rect and an ellipse share the same four numbers, so only the
              // element differs — the halo/stroke treatment is identical.
              const shapeProps =
                r.type === 'ellipse'
                  ? { cx: r.x + r.w / 2, cy: r.y + r.h / 2, rx: r.w / 2, ry: r.h / 2 }
                  : { x: r.x, y: r.y, width: r.w, height: r.h };
              const Tag = (r.type === 'ellipse' ? 'ellipse' : 'rect') as 'ellipse' | 'rect';
              return (
                <g
                  key={i}
                  style={{ pointerEvents: i < regions.length ? 'auto' : 'none', cursor: 'pointer' }}
                  onMouseDown={(e) => {
                    // Clicking an existing shape selects it instead of starting
                    // a new drag on top of it.
                    e.stopPropagation();
                    onSelect(i === selected ? null : i);
                  }}
                >
                  <Tag {...shapeProps} fill={c} fillOpacity={isSelected ? 0.3 : 0.18} stroke="#000" strokeOpacity={0.85} strokeWidth={4} vectorEffect="non-scaling-stroke" />
                  <Tag
                    {...shapeProps}
                    fill="none"
                    stroke={c}
                    strokeWidth={isSelected ? 3 : 2}
                    strokeDasharray={isSelected ? '6 3' : undefined}
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              );
            })}
          </svg>

          {/*
            Defect names as an HTML layer rather than SVG <text>.
            The overlay uses viewBox="0 0 1 1" with preserveAspectRatio="none",
            so anything drawn in it is stretched by the image's aspect ratio —
            a label would come out distorted and, at that scale, sub-pixel.
            Positioning HTML by percentage sidesteps both.
          */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {all.map((r, i) => {
              const dt = defectTypes.find((d) => d.id === r.defect_type_id);
              const isSelected = i === selected;
              // An unclassified shape says so, rather than showing nothing —
              // a blank label reads as "no label needed".
              const text = dt ? dt.name : 'unclassified';
              return (
                <div
                  key={`label-${i}`}
                  style={{
                    position: 'absolute',
                    left: `${r.x * 100}%`,
                    // Sit above the shape, unless it is against the top edge.
                    top: `${r.y * 100}%`,
                    transform: r.y > 0.06 ? 'translateY(-100%)' : 'translateY(0)',
                    maxWidth: '60%',
                    padding: '1px 6px',
                    borderRadius: 4,
                    // Solid fill, dark text: a defect colour behind pale text
                    // is unreadable on a bright part and vice versa.
                    background: dt ? dt.color : UNCLASSIFIED_COLOR,
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.9)',
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    opacity: isSelected ? 1 : 0.9,
                    outline: isSelected ? '2px solid #fff' : 'none',
                    // Counter the canvas zoom so the name stays readable at any
                    // magnification instead of growing into the picture.
                    transformOrigin: 'bottom left',
                    scale: String(1 / view.scale),
                  }}
                >
                  {text}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
